import { Button } from '@jouzen/ecom-components/v3/Button';
import { OUT_OF_STOCK } from '@jouzen/ecom-components/v3/consts/stockStatus';
import { Modal } from '@jouzen/ecom-components/v4/Modal';
import { useExperiment } from '@statsig/react-bindings';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import useUsaOnly from '@/app/hooks/useUsaOnly';

import type { BuildHardwareOrderBaseProps } from '../_types/order';
import CtaContent from './CtaContent';
import { CurrentContent } from './CurrentContent';

interface AddOnModalProps
  extends Pick<BuildHardwareOrderBaseProps, 'order' | 'yearlyMembership'> {
  readonly 'data-cy'?: string;
  readonly isAddingToCart: boolean;
  readonly isSelectingSize: boolean;
  readonly location: 'summary card' | 'sticky';
  readonly onAddToCart: () => Promise<void>;
  readonly onRadioChange: (value: string) => void;
  readonly ringCount: number;
}

const AddOnModal = ({
  'data-cy': dataCy = 'add-to-cart-button',
  isAddingToCart,
  isSelectingSize,
  location,
  onAddToCart,
  onRadioChange,
  order,
  ringCount,
  yearlyMembership,
}: AddOnModalProps) => {
  const t = useTranslations();
  const path = usePathname();
  const isUS = useUsaOnly();
  const showNewModal = useExperiment('membership_modal').get(
    'showNewModal',
    false,
  );

  const isOutOfStock = order.product.stockStatus === OUT_OF_STOCK;
  const disabled = isAddingToCart || isSelectingSize || isOutOfStock;

  // analytics for modal open
  const handleChange = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'add_to_cart',
      action: 'open_bundle_modal',
      path,
      location,
    });
  };

  return (
    <Modal
      onOpenChange={handleChange}
      triggerButton={(props) => (
        <Button
          {...props}
          className="w-full max-lg:text-sm"
          data-cy={dataCy}
          disabled={disabled}
          loading={isAddingToCart}
          loadingTextEnabled
          size="large"
          variant="primary"
        >
          {t('pdp_add_to_cart')}
        </Button>
      )}
      variant="large"
    >
      {({ titleProps }) =>
        showNewModal ? (
          <CtaContent
            onAddToCart={onAddToCart}
            onRadioChange={onRadioChange}
            order={order}
            titleProps={titleProps}
            yearlyMembership={yearlyMembership}
          />
        ) : (
          <CurrentContent
            isAddingToCart={isAddingToCart}
            isUS={isUS}
            onAddToCart={onAddToCart}
            onRadioChange={onRadioChange}
            order={order}
            ringCount={ringCount}
            titleProps={titleProps}
            yearlyMembership={yearlyMembership}
          />
        )
      }
    </Modal>
  );
};

export default AddOnModal;
